import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScenarioLockControllerApi, LockResponseStatusEnum } from '../api/generated';
import type { LockStatusResponse } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { useAppDispatch } from '../store/hooks';

const POLL_INTERVAL_MS = 10_000;
const RENEW_BUFFER_MS = 5_000;

interface ScenarioLockResult {
  isLocked: boolean;
  lockedByCurrentUser: boolean;
  lockedByName: string | null;
  acquireLock: () => Promise<boolean>;
  releaseLock: () => Promise<void>;
}

export function useScenarioLock(locationId?: string, scenarioId?: string): ScenarioLockResult {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [isLocked, setIsLocked] = useState(false);
  const [lockedByCurrentUser, setLockedByCurrentUser] = useState(false);
  const [lockedByName, setLockedByName] = useState<string | null>(null);
  const apiRef = useRef<ScenarioLockControllerApi | null>(null);
  const renewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRenewTimer = useCallback(() => {
    if (renewTimerRef.current) {
      clearTimeout(renewTimerRef.current);
      renewTimerRef.current = null;
    }
  }, []);

  const scheduleRenew = useCallback((expiresAt: string) => {
    clearRenewTimer();
    const expiresMs = new Date(expiresAt).getTime();
    const delay = Math.max(expiresMs - Date.now() - RENEW_BUFFER_MS, 1_000);
    renewTimerRef.current = setTimeout(() => {
      if (!apiRef.current || !locationId || !scenarioId) return;
      void apiRef.current.acquire({ locationId, scenarioId }).then((response) => {
        const { status, expiresAt: newExpiry } = response.data;
        if (
          (status === LockResponseStatusEnum.Acquired ||
            status === LockResponseStatusEnum.Renewed) &&
          newExpiry
        ) {
          scheduleRenew(newExpiry);
        }
      });
    }, delay);
  }, [clearRenewTimer, locationId, scenarioId]);

  const updateStatus = useCallback(async () => {
    if (!apiRef.current || !locationId || !scenarioId) return;
    try {
      const response = await apiRef.current.getStatus({ locationId, scenarioId });
      const data: LockStatusResponse = response.data;
      const lockedBy = data.lockedByName;
      setIsLocked(lockedBy != null);
      setLockedByCurrentUser(data.lockedByCurrentUser);
      setLockedByName(
        lockedBy
          ? `${lockedBy.firstName ?? ''} ${lockedBy.lastName ?? ''}`.trim() || null
          : null
      );
    } catch {
      setIsLocked(false);
      setLockedByCurrentUser(false);
      setLockedByName(null);
    }
  }, [locationId, scenarioId]);

  useEffect(() => {
    if (!locationId || !scenarioId) return;

    apiRef.current = new ScenarioLockControllerApi(createApiConfiguration());

    void updateStatus();
    const interval = setInterval(() => { void updateStatus(); }, POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      clearRenewTimer();
    };
  }, [locationId, scenarioId, updateStatus, clearRenewTimer]);

  const acquireLock = useCallback(async (): Promise<boolean> => {
    if (!locationId || !scenarioId || !apiRef.current) return false;
    await updateStatus();
    try {
      const response = await apiRef.current.acquire({ locationId, scenarioId });
      const { status, expiresAt } = response.data;
      const acquired =
        status === LockResponseStatusEnum.Acquired ||
        status === LockResponseStatusEnum.Renewed;
      if (acquired) {
        setIsLocked(true);
        setLockedByCurrentUser(true);
        if (expiresAt) scheduleRenew(expiresAt);
      }
      return acquired;
    } catch (error) {
      console.error('Failed to acquire scenario lock:', error);
      dispatch(showError({ message: t('lock-acquire-error') }));
      return false;
    }
  }, [locationId, scenarioId, updateStatus, scheduleRenew, dispatch, t]);

  const releaseLock = useCallback(async () => {
    if (!locationId || !scenarioId || !apiRef.current) return;
    clearRenewTimer();
    try {
      await apiRef.current.release({ locationId, scenarioId });
      setIsLocked(false);
      setLockedByCurrentUser(false);
      setLockedByName(null);
    } catch {
      // ignore release failures
    }
  }, [locationId, scenarioId, clearRenewTimer]);

  return { isLocked, lockedByCurrentUser, lockedByName, acquireLock, releaseLock };
}
